"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowLeft,
  ArrowRight,
  BookOpenText,
  Check,
  CheckCircle2,
  ChevronRight,
  ClipboardCheck,
  Clock3,
  ExternalLink,
  FileCheck2,
  Globe2,
  GraduationCap,
  HardHat,
  Headphones,
  IdCard,
  MapPin,
  ShieldCheck,
  TrainFront,
  TriangleAlert,
  Users,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";

import styles from "./pre-class-preparation.module.css";
import ToastHelper from "@/utils/toastHelper";
import { preClassPreparationService } from "@/services/preClassPreparationService";

const steps = [
  { short: "Start", title: "Before you begin", icon: BookOpenText },
  { short: "Travel", title: "Class location and travel", icon: MapPin },
  { short: "Prepare", title: "Mandatory pre-class requirements", icon: ClipboardCheck },
  { short: "Bring", title: "What to bring and wear", icon: HardHat },
  { short: "Attend", title: "Attendance and punctuality", icon: Clock3 },
  { short: "Assess", title: "Training and assessment", icon: GraduationCap },
  { short: "Support", title: "Support, safety and your rights", icon: Users },
  { short: "Check", title: "Knowledge check", icon: FileCheck2 },
  { short: "Declare", title: "Final declaration", icon: ShieldCheck },
];

const questions = [
  {
    id: "delivery",
    question: "How is approved White Card training completed in Victoria?",
    options: [
      { value: "a", label: "Entirely online from home." },
      { value: "b", label: "Face-to-face in a classroom with an approved RTO." },
      { value: "c", label: "By reading the learner guide only." },
    ],
    correct: "b",
    review: "Review Step 1: Before you begin.",
  },
  {
    id: "arrival",
    question: "How early should you arrive for check-in before your class starts?",
    options: [
      { value: "a", label: "30 minutes before class starts." },
      { value: "b", label: "15 minutes after training has started." },
      { value: "c", label: "At any time during the session." },
    ],
    correct: "a",
    review: "Review Step 5: Attendance and punctuality.",
  },
  {
    id: "identity",
    question: "Which item must you bring for identity verification?",
    options: [
      { value: "a", label: "A photograph of another person's identification." },
      { value: "b", label: "A social media profile." },
      {
        value: "c",
        label:
          "Current government-issued photo identification accepted by GIT that matches your enrolment details.",
      },
    ],
    correct: "c",
    review: "Review Step 4: What to bring and wear.",
  },
  {
    id: "requirements",
    question: "When must the required portal preparation be completed?",
    options: [
      { value: "a", label: "Before attending the class." },
      { value: "b", label: "Only after the White Card arrives." },
      { value: "c", label: "It is optional." },
    ],
    correct: "a",
    review: "Review Step 3: Mandatory pre-class requirements.",
  },
  {
    id: "assessment",
    question: "Which statement about assessment and PPE is correct?",
    options: [
      { value: "a", label: "Attendance automatically means you are Competent." },
      { value: "b", label: "Another person may answer assessment questions for you." },
      {
        value: "c",
        label:
          "You must complete your own written, oral and practical tasks, including selecting and correctly fitting the required PPE.",
      },
    ],
    correct: "c",
    review: "Review Step 6: Training and assessment.",
  },
];

const pakenhamMapUrl =
  "https://www.google.com/maps/search/?api=1&query=4%2F70%20Main%20Street%2C%20Pakenham%20VIC%203810";
const northMelbourneMapUrl =
  "https://www.google.com/maps/search/?api=1&query=15-17%20Racecourse%20Road%2C%20North%20Melbourne%20VIC%203051";

function TickList({ items }) {
  return (
    <ul className={styles.tickList}>
      {items.map((item) => (
        <li key={item}>
          <span aria-hidden="true">
            <Check />
          </span>
          {item}
        </li>
      ))}
    </ul>
  );
}

export default function PreClassPreparationGuide({ courseUrl, studentBookingId, mode = "student" }) {
  const [currentStep, setCurrentStep] = useState(0);
  const [highestStep, setHighestStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [requirementsConfirmed, setRequirementsConfirmed] = useState(false);
  const [answers, setAnswers] = useState({});
  const [quizChecked, setQuizChecked] = useState(false);
  const [quizMessage, setQuizMessage] = useState("");
  const [declarationAccepted, setDeclarationAccepted] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [speaking, setSpeaking] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const headingRef = useRef(null);

  const progress = ((currentStep + 1) / steps.length) * 100;
  const answeredCount = useMemo(
    () => questions.filter((question) => Boolean(answers[question.id])).length,
    [answers],
  );

  useEffect(() => {
    if (currentStep === 0) return;
    headingRef.current?.focus();
  }, [currentStep]);

  useEffect(() => {
    return () => window.speechSynthesis?.cancel();
  }, []);

  useEffect(() => {
    if (studentBookingId <= 0) { setLoading(false); return; }
    let mounted = true;
    preClassPreparationService.get(studentBookingId).then((data) => {
      if (!mounted) return;
      const saved = data?.preparationData ?? {};
      const savedCompleted = Array.isArray(saved.completedSteps) ? saved.completedSteps.filter((step) => Number.isInteger(step) && step >= 0 && step < steps.length) : [];
      const restoredStep = Math.min(steps.length - 1, Math.max(0, Number(data?.currentStepNo || 1) - 1));
      setCompletedSteps(savedCompleted);
      setRequirementsConfirmed(saved.requirementsConfirmed === true);
      setAnswers(saved.answers ?? {});
      setDeclarationAccepted(saved.declarationAccepted === true);
      setCurrentStep(restoredStep);
      setHighestStep(mode === "admin" ? steps.length - 1 : Math.max(restoredStep, savedCompleted.length ? Math.min(steps.length - 1, Math.max(...savedCompleted) + 1) : 0));
      setSubmitted(Number(data?.preparationStatusNo) === 2);
    }).catch((error) => ToastHelper.apiError(error, "Unable to load the pre-class preparation"))
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, [mode, studentBookingId]);

  function buildRequest(currentStepNo, nextCompletedSteps = completedSteps, accepted = declarationAccepted) {
    return {
      studentBookingId,
      preparationVersion: 1,
      currentStepNo,
      preparationData: { version: 1, completedSteps: nextCompletedSteps, requirementsConfirmed, answers, declarationAccepted: accepted },
    };
  }

  function moveToStep(index) {
    if (index > highestStep) return;
    window.speechSynthesis?.cancel();
    setSpeaking(false);
    setCurrentStep(index);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  async function continueForward() {
    if (mode === "admin") {
      setCurrentStep((current) => Math.min(current + 1, steps.length - 1));
      return;
    }
    if (currentStep === 2 && !requirementsConfirmed) return;

    if (currentStep === 7) {
      setQuizChecked(true);
      if (answeredCount < questions.length) {
        setQuizMessage("Please answer all five questions before continuing.");
        return;
      }

      const incorrect = questions.filter(
        (question) => answers[question.id] !== question.correct,
      );
      if (incorrect.length > 0) {
        setQuizMessage(
          `${incorrect.length} ${incorrect.length === 1 ? "answer needs" : "answers need"} another look. Review the guidance shown below and try again.`,
        );
        return;
      }

      setQuizMessage("All five answers are correct. You can continue to the declaration.");
    }

    const nextCompletedSteps = completedSteps.includes(currentStep) ? completedSteps : [...completedSteps, currentStep];
    const nextStep = Math.min(currentStep + 1, steps.length - 1);
    setSaving(true);
    try {
      if (mode !== "admin") await preClassPreparationService.save(buildRequest(nextStep + 1, nextCompletedSteps));
      setCompletedSteps(nextCompletedSteps);
      setHighestStep((current) => Math.max(current, nextStep));
      setCurrentStep(nextStep);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (error) {
      ToastHelper.apiError(error, "Unable to save the pre-class preparation");
    } finally {
      setSaving(false);
    }
  }

  function goBack() {
    if (currentStep === 0) {
      window.location.href = courseUrl;
      return;
    }
    setCurrentStep((current) => Math.max(0, current - 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  function toggleSpeech() {
    if (!("speechSynthesis" in window)) return;

    if (speaking) {
      window.speechSynthesis.cancel();
      setSpeaking(false);
      return;
    }

    const content = document.getElementById("active-step-content")?.innerText;
    if (!content) return;

    const utterance = new SpeechSynthesisUtterance(content);
    utterance.lang = "en-AU";
    utterance.rate = 0.95;
    utterance.onend = () => setSpeaking(false);
    utterance.onerror = () => setSpeaking(false);
    window.speechSynthesis.cancel();
    window.speechSynthesis.speak(utterance);
    setSpeaking(true);
  }

  async function submitPreparation() {
    if (!declarationAccepted) return;
    setSaving(true);
    try {
      const finalCompletedSteps = steps.map((_, index) => index);
      if (mode !== "admin") await preClassPreparationService.submit(buildRequest(9, finalCompletedSteps, true));
      setCompletedSteps(finalCompletedSteps);
      setSubmitted(true);
      window.location.href = courseUrl;
    } catch (error) {
      ToastHelper.apiError(error, "Unable to complete the pre-class preparation");
    } finally {
      setSaving(false);
    }
  }

  function renderStep() {
    switch (currentStep) {
      case 0:
        return (
          <>
            <div className={styles.timeBeacon}>
              <span className={styles.beaconIcon} aria-hidden="true">
                <Clock3 />
              </span>
              <div>
                <span>Class starts</span>
                <strong>As stated in your booking confirmation</strong>
              </div>
              <div>
                <span>Arrive for check-in</span>
                <strong>30 minutes before class starts</strong>
              </div>
              <div>
                <span>Delivery</span>
                <strong>Face-to-face</strong>
              </div>
            </div>

            <div className={styles.noticeBox}>
              <TriangleAlert aria-hidden="true" />
              <p>
                <strong>The online portal is for preparation only.</strong> Victorian White Card training and assessment must be completed face-to-face with an approved RTO.
              </p>
            </div>

            <div className={styles.introGrid}>
              <article>
                <span aria-hidden="true"><IdCard /></span>
                <h3>Have your booking ready</h3>
                <p>Check that your legal name, class date and GIT location are correct before continuing.</p>
              </article>
              <article>
                <span aria-hidden="true"><ClipboardCheck /></span>
                <h3>Complete every step</h3>
                <p>Work through the preparation, answer five questions and submit the final declaration.</p>
              </article>
              <article>
                <span aria-hidden="true"><Headphones /></span>
                <h3>Ask for support early</h3>
                <p>Contact GIT before class if you need help, an accessible format or reasonable adjustment.</p>
              </article>
            </div>
          </>
        );

      case 1:
        return (
          <>
            <div className={styles.noticeBox}>
              <MapPin aria-hidden="true" />
              <p>
                <strong>Attend only the address in your booking confirmation.</strong> Viewing another location here does not change your booking. Contact GIT before travelling if the details do not match.
              </p>
            </div>

            <div className={styles.locationStack}>
              <article className={styles.locationCard}>
                <div className={styles.locationCopy}>
                  <span className={styles.locationLabel}>Pakenham</span>
                  <h3>Suite 4, 70 Main Street</h3>
                  <p className={styles.addressLine}>Pakenham VIC 3810</p>
                  <div className={styles.transportItem}>
                    <TrainFront aria-hidden="true" />
                    <p><strong>Train and bus:</strong> Take the Pakenham Line to Pakenham Station, then walk about 8–10 minutes. Local buses also connect at the station.</p>
                  </div>
                  <p className={styles.arrivalNote}>Allow extra travel time and arrive at GIT 30 minutes before your class starts.</p>
                  <Button asChild variant="outline" className={styles.mapButton}>
                    <a href={pakenhamMapUrl} target="_blank" rel="noreferrer">
                      Open live Google Map <ExternalLink aria-hidden="true" />
                    </a>
                  </Button>
                </div>
                <figure className={styles.mapFigure}>
                  <img
                    src="/images/git-pakenham-google-map.png"
                    alt="Google Maps view of Suite 4, 70 Main Street, Pakenham, including nearby streets and Pakenham Station"
                    width={1364}
                    height={930}
                  />
                  <figcaption>Google Maps location reference. On-image attribution is retained. Check the live map for current directions.</figcaption>
                </figure>
              </article>

              <article className={styles.locationCard}>
                <div className={styles.locationCopy}>
                  <span className={`${styles.locationLabel} ${styles.blueLabel}`}>North Melbourne</span>
                  <h3>15–17 Racecourse Road</h3>
                  <p className={styles.addressLine}>North Melbourne VIC 3051</p>
                  <div className={styles.transportItem}>
                    <TrainFront aria-hidden="true" />
                    <p><strong>Tram and train:</strong> Route 57 tram Stop 22 is about 1–2 minutes on foot. Flemington Bridge Station on the Upfield Line is about 5–7 minutes on foot.</p>
                  </div>
                  <p className={styles.arrivalNote}>Allow extra time for service changes, road traffic and finding the correct entrance.</p>
                  <Button asChild variant="outline" className={styles.mapButton}>
                    <a href={northMelbourneMapUrl} target="_blank" rel="noreferrer">
                      Open live Google Map <ExternalLink aria-hidden="true" />
                    </a>
                  </Button>
                </div>
                <figure className={styles.mapFigure}>
                  <img
                    src="/images/git-north-melbourne-google-map.png"
                    alt="Google Maps view of 15 to 17 Racecourse Road, North Melbourne, including nearby tram stops and Flemington Bridge Station"
                    width={1364}
                    height={930}
                  />
                  <figcaption>Google Maps location reference. On-image attribution is retained. Check the live map for current directions.</figcaption>
                </figure>
              </article>
            </div>

            <div className={styles.travelFooter}>
              <div>
                <strong>Driving?</strong>
                <p>Nearby parking may be limited or time-restricted. Read every sign and allow enough time to park legally.</p>
              </div>
              <a href="https://transport.vic.gov.au/journey" target="_blank" rel="noreferrer">
                Plan with Transport Victoria <ExternalLink aria-hidden="true" />
              </a>
            </div>
          </>
        );

      case 2:
        return (
          <>
            <div className={styles.requirementGrid}>
              {[
                ["01", "Complete your enrolment", "Use your legal name and check your date of birth, contact details and postal address."],
                ["02", "Provide your identification", "Upload the requested identification clearly and bring acceptable current photo ID to class."],
                ["03", "Provide your USI", "Make sure your Unique Student Identifier matches your enrolment, or provide approved exemption evidence."],
                ["04", "Complete the LLN check", "Answer it yourself so GIT can identify any learning or language support you may need."],
                ["05", "Review the learning material", "Complete all required reading, videos and preparation activities shown in the portal."],
                ["06", "Complete the knowledge check", "Answer the five preparation questions. Review the information and try again if needed."],
                ["07", "Read the learner documents", "Read the current handbook, booking terms, fees and refunds, privacy, and complaints and appeals information."],
                ["08", "Confirm your booking", "Check that payment and other booking requirements are complete and confirm the correct date and location."],
              ].map(([number, title, text]) => (
                <article key={number}>
                  <span>{number}</span>
                  <div>
                    <h3>{title}</h3>
                    <p>{text}</p>
                  </div>
                </article>
              ))}
            </div>

            <label className={styles.confirmationRow} htmlFor="requirements-confirmed">
              <Checkbox
                id="requirements-confirmed"
                checked={requirementsConfirmed}
                disabled={mode === "admin"}
                onCheckedChange={(value) => setRequirementsConfirmed(value === true)}
              />
              <span>
                <strong>I confirm that my required pre-class items are complete.</strong>
                If something is incomplete, I will contact GIT before class.
              </span>
            </label>
          </>
        );

      case 3:
        return (
          <>
            <div className={styles.bringGrid}>
              <section>
                <span className={styles.sectionIcon} aria-hidden="true"><IdCard /></span>
                <h3>You must bring</h3>
                <TickList
                  items={[
                    "Current government-issued photo ID accepted by GIT. The name must match your enrolment.",
                    "Your USI, or approved exemption evidence if this applies to you.",
                    "Your booking confirmation, printed or available on your phone.",
                    "A blue or black pen.",
                    "Any reading glasses, hearing aids, prescribed medication or personal support equipment you normally use.",
                    "Water and lunch or snacks for the day.",
                  ]}
                />
              </section>
              <section>
                <span className={styles.sectionIcon} aria-hidden="true"><HardHat /></span>
                <h3>Wear and PPE</h3>
                <TickList
                  items={[
                    "Comfortable, practical clothing that allows you to participate safely.",
                    "Enclosed, flat footwear. Do not wear thongs, open-toe sandals or high heels.",
                    "GIT will make the assessment PPE available: eye protection, hearing protection, a hard hat and high-visibility clothing.",
                    "You must select suitable PPE and correctly fit it to yourself while the assessor observes.",
                    "You may use your own clean, serviceable PPE only if the trainer approves it.",
                  ]}
                />
              </section>
            </div>
            <div className={`${styles.noticeBox} ${styles.amberNotice}`}>
              <TriangleAlert aria-hidden="true" />
              <p><strong>Fit to participate:</strong> Do not attend if alcohol, illegal drugs, illness or medication makes it unsafe for you to participate. Contact GIT as early as possible.</p>
            </div>
          </>
        );

      case 4:
        return (
          <>
            <div className={styles.attendanceHero}>
              <div>
                <span>Check-in opens</span>
                <strong>30 minutes before class starts</strong>
              </div>
              <ChevronRight aria-hidden="true" />
              <div>
                <span>Aim to arrive</span>
                <strong>30 minutes before class starts</strong>
              </div>
              <ChevronRight aria-hidden="true" />
              <div className={styles.startTime}>
                <span>Training starts</span>
                <strong>As stated in your booking confirmation</strong>
              </div>
            </div>
            <div className={styles.attendanceList}>
                <article><span>1</span><div><h3>Be ready before training starts</h3><p>Finish identity checks, sign in and be seated with your materials ready before training starts.</p></div></article>
              <article><span>2</span><div><h3>Stay for the full session</h3><p>Victorian White Card training runs for at least six hours. Allow the full day, including scheduled breaks, and follow the finish time in your booking.</p></div></article>
              <article><span>3</span><div><h3>Complete every activity</h3><p>Participate in learning, written and oral questions, practical demonstrations, and attendance records.</p></div></article>
              <article><span>4</span><div><h3>Keep your phone silent</h3><p>Put it away during training and assessment unless the trainer permits its use. Tell the trainer if you expect an urgent call.</p></div></article>
            </div>
            <div className={`${styles.noticeBox} ${styles.redNotice}`}>
              <TriangleAlert aria-hidden="true" />
              <p><strong>Late arrival:</strong> If training has started, GIT may be unable to admit you because you have missed mandatory training time. You may need to book another session. Current booking terms will apply.</p>
            </div>
          </>
        );

      case 5:
        return (
          <>
            <div className={styles.assessmentGrid}>
              <section>
                <span className={styles.sectionIcon} aria-hidden="true"><BookOpenText /></span>
                <h3>What you will learn</h3>
                <TickList items={[
                  "Construction hazards, risks and ways to control them.",
                  "How to report hazards, incidents, injuries and emergencies.",
                  "Safety signs, tags, JSA, SWMS and SDS documents.",
                  "WHS roles, responsibilities and safe work practices.",
                  "How to select, use and correctly fit PPE.",
                ]} />
              </section>
              <section>
                <span className={styles.sectionIcon} aria-hidden="true"><GraduationCap /></span>
                <h3>How you will be assessed</h3>
                <TickList items={[
                  "Written questions completed by you.",
                  "Oral questions or reports given to the trainer.",
                  "Practical demonstrations observed by a qualified assessor.",
                  "Your own work, knowledge and skills—do not copy or share answers.",
                  "Safe participation and compliance with reasonable instructions.",
                ]} />
              </section>
            </div>
            <div className={styles.outcomeBand}>
              <div><strong>Competent</strong><p>The assessor has enough valid evidence that you meet all unit requirements.</p></div>
              <div><strong>Not Yet Competent</strong><p>You need more training or evidence. Your assessor will explain feedback and any reassessment options under GIT policy.</p></div>
            </div>
            <div className={styles.infoPanel}>
              <ShieldCheck aria-hidden="true" />
              <div>
                <h3>After successful completion</h3>
                <p>After you are assessed as Competent and all identity, USI and administrative requirements are complete, GIT will process the required records and submit the information to WorkSafe Victoria. WorkSafe advises that the card may take up to 60 days to arrive by mail.</p>
                <p>A White Card does not replace a workplace or site-specific induction.</p>
              </div>
            </div>
          </>
        );

      case 6:
        return (
          <>
            <div className={styles.supportGrid}>
              <section>
                <span className={styles.sectionIcon} aria-hidden="true"><Users /></span>
                <h3>Learning support and reasonable adjustment</h3>
                <p>Tell GIT before class if disability, injury, reading, writing, numeracy, English language, anxiety, hearing, vision or another circumstance may affect your participation.</p>
                <p>GIT will discuss suitable support with you. An adjustment cannot remove an essential competency, safety or WorkSafe requirement, and another person cannot give your assessment answers.</p>
              </section>
              <section>
                <span className={styles.sectionIcon} aria-hidden="true"><ShieldCheck /></span>
                <h3>Classroom conduct and safety</h3>
                <TickList items={[
                  "Treat learners, staff and visitors respectfully.",
                  "Follow emergency instructions and report hazards, injury, illness or unsafe conditions immediately.",
                  "Use facilities and equipment carefully.",
                  "Keep personal and assessment information private.",
                ]} />
              </section>
            </div>
            <section className={styles.rightsPanel}>
              <div>
                <span className={styles.sectionEyebrow}>Questions, complaints and appeals</span>
                <h3>You can raise a concern without being disadvantaged</h3>
                <p>Speak with your trainer or GIT staff as early as possible. You may give feedback, make a complaint about GIT or a service delivered on its behalf, or appeal an assessment result or another decision that affects you.</p>
                <p>You may ask for help and have a support person or representative. GIT should provide an impartial process, explain the outcome and provide access to independent review where required by its current policy.</p>
              </div>
              <address>
                <strong>Contact GIT</strong>
                <a href="mailto:info@git.vic.edu.au">info@git.vic.edu.au</a>
                <a href="tel:+61359415070">03 5941 5070</a>
                <a href="https://www.git.vic.edu.au/FormandPolicy.aspx" target="_blank" rel="noreferrer">Forms and policies <ExternalLink aria-hidden="true" /></a>
              </address>
            </section>
          </>
        );

      case 7:
        return (
          <>
            <div className={styles.quizSummary}>
              <div><strong>{answeredCount} of 5</strong><span>questions answered</span></div>
              <p>Choose one answer for each question. If an answer is incorrect, review the guidance and try again.</p>
            </div>
            <div className={styles.quizList}>
              {questions.map((question, questionIndex) => {
                const incorrect = quizChecked && answers[question.id] !== question.correct;
                const descriptionId = `${question.id}-feedback`;
                return (
                  <fieldset className={incorrect ? styles.questionError : ""} key={question.id}>
                    <legend><span>{questionIndex + 1}</span>{question.question}</legend>
                    <RadioGroup
                      value={answers[question.id] ?? ""}
                      disabled={mode === "admin"}
                      onValueChange={(value) => {
                        setAnswers((current) => ({ ...current, [question.id]: value }));
                        setQuizChecked(false);
                        setQuizMessage("");
                      }}
                      aria-describedby={incorrect ? descriptionId : undefined}
                    >
                      {question.options.map((option) => {
                        const inputId = `${question.id}-${option.value}`;
                        return (
                          <label className={styles.radioOption} htmlFor={inputId} key={option.value}>
                            <RadioGroupItem id={inputId} value={option.value} aria-invalid={incorrect} />
                            <span><strong>{option.value.toUpperCase()}.</strong>{option.label}</span>
                          </label>
                        );
                      })}
                    </RadioGroup>
                    {incorrect && <p className={styles.questionFeedback} id={descriptionId}>{question.review}</p>}
                  </fieldset>
                );
              })}
            </div>
            {quizMessage && (
              <div className={quizMessage.startsWith("All") ? styles.successMessage : styles.errorMessage} role="status">
                {quizMessage.startsWith("All") ? <CheckCircle2 aria-hidden="true" /> : <TriangleAlert aria-hidden="true" />}
                <p>{quizMessage}</p>
              </div>
            )}
          </>
        );

      case 8:
        return (
          <>
            <div className={styles.declarationPanel}>
              <p>By selecting the checkbox and submitting, I confirm that:</p>
              <TickList items={[
                "I completed the required preparation myself and the information I provided is true and accurate.",
                "My legal name, date of birth, contact details, USI and postal address are correct, or I contacted GIT to correct them.",
                "I checked my class date and location and will attend the address in my booking confirmation.",
                "I will arrive 30 minutes before class starts and be ready when training begins.",
                "I will bring acceptable current photo ID, my USI or exemption evidence, my booking confirmation and a pen.",
                "I understand the class is face-to-face, runs for at least six hours and requires my full participation.",
                "I will complete my own assessment work and demonstrate the correct selection and fitting of PPE.",
                "I know how to contact GIT about support, reasonable adjustment, a complaint or an appeal.",
                "I have read the current booking terms, Client Information Handbook, fees and refunds, privacy, and complaints and appeals information provided in the portal.",
              ]} />
            </div>
            <label className={`${styles.confirmationRow} ${styles.declarationCheck}`} htmlFor="declaration-accepted">
            <Checkbox
              id="declaration-accepted"
              checked={declarationAccepted}
              disabled={mode === "admin"}
                onCheckedChange={(value) => setDeclarationAccepted(value === true)}
              />
              <span>
                <strong>I have read and understood the declaration above.</strong>
                I understand that completing this preparation does not record a Competent result and does not guarantee that a White Card will be issued.
              </span>
            </label>
          </>
        );
    }
  }

  if (loading) return <div className={styles.pageShell}><main className={styles.successPage}><p>Loading pre-class preparation...</p></main></div>;

  if (submitted) {
    return (
      <div className={styles.pageShell}>
        <header className={styles.portalHeader}>
          <div className={styles.headerInner}>
            <a className={styles.brandLockup} href={courseUrl} aria-label="Return to course">
              <span className={styles.brandMark} aria-hidden="true">GIT</span>
              <span className={styles.brandCopy}><strong>Gippsland Institute</strong><span>of Technology</span></span>
            </a>
            <span className={styles.portalLabel}>Student portal</span>
          </div>
        </header>
        <main className={styles.successPage}>
          <span className={styles.successIcon} aria-hidden="true"><Check /></span>
          <span className={styles.sectionEyebrow}>Pre-class preparation submitted</span>
          <h1>Thank you. You are ready for class.</h1>
          <p>Your preparation has been completed. Keep your booking confirmation and bring the required original photo identification.</p>
          {mode === "admin" && (
            <section className={styles.successSchedule} aria-label="Learner preparation answers">
              <div><span>Staff view</span><strong>Read only</strong></div>
              <div><span>Requirements confirmed</span><strong>{requirementsConfirmed ? "Yes" : "No"}</strong></div>
              <div><span>Declaration accepted</span><strong>{declarationAccepted ? "Yes" : "No"}</strong></div>
              {questions.map((question) => (
                <div key={question.id}>
                  <span>{question.question}</span>
                  <strong>{question.options.find((option) => option.value === answers[question.id])?.label || "Not answered"}</strong>
                </div>
              ))}
            </section>
          )}
          <div className={styles.successSchedule}>
            <div><span>Arrive for check-in</span><strong>30 minutes before class starts</strong></div>
            <div><span>Class start</span><strong>As stated in your booking confirmation</strong></div>
            <div><span>Location</span><strong>Check your booking</strong></div>
          </div>
          <div className={styles.successActions}>
            <Button asChild><a href={courseUrl}>Return to course progress</a></Button>
          </div>
        </main>
      </div>
    );
  }

  const ActiveIcon = steps[currentStep].icon;

  return (
    <div className={styles.pageShell}>
      <a className={styles.skipLink} href="#active-step-content">Skip to current preparation step</a>

      <header className={styles.portalHeader}>
        <div className={styles.headerInner}>
          <a className={styles.brandLockup} href={courseUrl} aria-label="Return to course">
            <span className={styles.brandMark} aria-hidden="true">GIT</span>
            <span className={styles.brandCopy}><strong>Gippsland Institute</strong><span>of Technology</span></span>
          </a>
          <div className={styles.portalIdentity}>
            <span className={styles.portalLabel}>Student portal</span>
          </div>
        </div>
      </header>

      <main className={styles.main}>
        <nav className={styles.contextNav} aria-label="Breadcrumb">
          <a href={courseUrl}><ArrowLeft aria-hidden="true" />Course progress</a>
          <ChevronRight aria-hidden="true" />
          <span>White Card</span>
          <ChevronRight aria-hidden="true" />
          <span aria-current="page">Pre-Class Preparation</span>
          <div className={styles.accessibilityTools}>
            <span className={styles.languageChip}><Globe2 aria-hidden="true" />English (AU)</span>
            <Button
              variant="outline"
              size="sm"
              className={speaking ? styles.listenActive : styles.listenButton}
              onClick={toggleSpeech}
              aria-pressed={speaking}
            >
              <Headphones aria-hidden="true" />{speaking ? "Stop" : "Listen"}
            </Button>
          </div>
        </nav>

        <section className={styles.courseHeader} aria-labelledby="preclass-title">
          <div>
            <span className={styles.sectionEyebrow}>CPCWHS1001 · Required before class</span>
            <h1 id="preclass-title">White Card pre-class preparation</h1>
          </div>
          <div className={styles.progressBlock}>
            <div><span>Step {currentStep + 1} of {steps.length}</span><strong>{Math.round(progress)}%</strong></div>
            <Progress value={progress} aria-label={`${Math.round(progress)} percent through pre-class preparation`} />
          </div>
        </section>

        <div className={styles.portalWorkspace}>
          <aside className={styles.stepRail} aria-label="Preparation steps">
            <ol>
              {steps.map((step, index) => {
                const Icon = step.icon;
                const isComplete = completedSteps.includes(index);
                const isCurrent = currentStep === index;
                return (
                  <li key={step.short}>
                    <Button
                      variant="ghost"
                      className={`${styles.stepButton} ${isCurrent ? styles.currentStep : ""} ${isComplete ? styles.completeStep : ""}`}
                      onClick={() => moveToStep(index)}
                      disabled={index > highestStep}
                      aria-current={isCurrent ? "step" : undefined}
                    >
                      <span className={styles.stepMarker} aria-hidden="true">{isComplete ? <Check /> : <Icon />}</span>
                      <span><small>{String(index + 1).padStart(2, "0")}</small><strong>{step.short}</strong></span>
                    </Button>
                  </li>
                );
              })}
            </ol>
            <div className={styles.classReminder}>
              <span>Need help?</span>
              <a href="tel:+61359415070">Call 03 5941 5070</a>
            </div>
          </aside>

          <section className={styles.stepCard} id="active-step-content" aria-labelledby="active-step-title">
            <header className={styles.stepHeader}>
              <span className={styles.activeIcon} aria-hidden="true"><ActiveIcon /></span>
              <div>
                <span>Step {currentStep + 1} of {steps.length}</span>
                <h2 id="active-step-title" ref={headingRef} tabIndex={-1}>{steps[currentStep].title}</h2>
              </div>
            </header>

            <div className={styles.stepBody}>{renderStep()}</div>

            <footer className={styles.stepActions}>
              <Button variant="outline" onClick={goBack}><ArrowLeft aria-hidden="true" />Back</Button>
              {currentStep < steps.length - 1 ? (
                <Button
                  className={styles.continueButton}
                  onClick={continueForward}
                  disabled={saving || (mode !== "admin" && currentStep === 2 && !requirementsConfirmed)}
                >
                  {saving ? "Saving..." : mode === "admin" ? "Next page" : currentStep === 7 ? "Check answers" : "Continue"}<ArrowRight aria-hidden="true" />
                </Button>
              ) : mode === "admin" ? (
                <Button asChild className={styles.submitButton}><a href={courseUrl}>Return to course progress</a></Button>
              ) : (
                <Button className={styles.submitButton} onClick={submitPreparation} disabled={!declarationAccepted || saving}>
                  <CheckCircle2 aria-hidden="true" />{saving ? "Submitting..." : "Submit preparation"}
                </Button>
              )}
            </footer>
          </section>
        </div>
      </main>

      <footer className={styles.footer}>
        <div><span>Gippsland Institute of Technology</span><span>RTO 45698</span><span>CRICOS 03921A</span></div>
        <p>Need help? <a href="mailto:info@git.vic.edu.au">info@git.vic.edu.au</a> · <a href="tel:+61359415070">03 5941 5070</a></p>
      </footer>
    </div>
  );
}
